import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { segmentApi } from '@edulastic/api'
import { EduJoyride } from '@edulastic/common'
import { tutorials } from '@edulastic/constants'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import { getProfileReportsJoyrideVisit } from '../ducks'
import { getUserId } from '../../src/selectors/user'
import {
  PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
  PROFILE_REPORTS_JOYRIDE_TARGET,
  PROFILE_REPORTS_JOYRIDE_START_DELAY_MS,
  markProfileReportsJoyrideVisit1CompletedThisSession,
  profileReportsJoyrideStepsVisit1,
  profileReportsJoyrideStepsVisit2,
} from '../constants'
import {
  scrollJoyrideTargetIntoView,
  waitForStableJoyrideTarget,
} from '../utils/profileReportsJoyrideUtils'
import {
  setViewedTutorialsAction,
  viewedTutorialsSelector,
} from '../../../tutorials/tutorialReducer'

const PROFILE_REPORTS_JOYRIDE_LOCALE = {
  close: 'Okay, got it',
}

const ProfileReportsJoyride = ({
  visit,
  userId,
  viewedTutorials,
  setViewedTutorials,
}) => {
  const [run, setRun] = useState(false)
  const isVisit1 = visit === 1

  useEffect(() => {
    if (!visit) {
      setRun(false)
      return undefined
    }

    let cancelled = false
    let startTimer

    setRun(false)

    waitForStableJoyrideTarget(PROFILE_REPORTS_JOYRIDE_TARGET).then(
      (target) => {
        if (cancelled || !target) return

        scrollJoyrideTargetIntoView(target)

        // Match dashboard overview joyride: remeasure after scroll settles.
        startTimer = setTimeout(() => {
          if (!cancelled) {
            setRun(true)
          }
        }, PROFILE_REPORTS_JOYRIDE_START_DELAY_MS)
      }
    )

    return () => {
      cancelled = true
      clearTimeout(startTimer)
      setRun(false)
    }
  }, [visit])

  const profileReportsJoyrideCallback = useCallback(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        setRun(false)
        return
      }

      if (data.lifecycle !== LIFECYCLE.COMPLETE) return

      const { CLOSE, NEXT, LAST } = ACTIONS
      const isCtaComplete = data.action === NEXT || data.action === LAST
      const isClose = data.action === CLOSE

      if (isVisit1) {
        if (!isCtaComplete && !isClose) return
        markProfileReportsJoyrideVisit1CompletedThisSession(userId)
        setViewedTutorials([
          ...viewedTutorials,
          tutorials.profileReportsJoyrideVisit1,
        ])
        segmentApi.genericEventTrack('Joyride', {
          userResponse: 'clickCTA',
          CTA_Name: PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
          pagePath: window.location.pathname,
          popup_occurrence: 1,
        })
      } else {
        if (!isClose && !isCtaComplete) return
        setViewedTutorials([
          ...viewedTutorials,
          tutorials.profileReportsJoyrideVisit2,
        ])
        segmentApi.genericEventTrack('Joyride', {
          userResponse: isClose ? 'Close' : 'clickCTA',
          CTA_Name: PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
          pagePath: window.location.pathname,
          popup_occurrence: 2,
        })
      }

      setRun(false)
    },
    [isVisit1, setViewedTutorials, userId, viewedTutorials]
  )

  if (!visit) {
    return null
  }

  return (
    <EduJoyride
      hideCloseButton={isVisit1}
      run={run}
      disableScrolling
      steps={
        isVisit1
          ? profileReportsJoyrideStepsVisit1
          : profileReportsJoyrideStepsVisit2
      }
      callback={profileReportsJoyrideCallback}
      locale={PROFILE_REPORTS_JOYRIDE_LOCALE}
      styles={{
        options: { width: isVisit1 ? 360 : 352, zIndex: 1015 },
        tooltipTitle: { display: 'none' },
        tooltipFooter: {
          justifyContent: 'flex-start',
          marginTop: 16,
        },
        tooltipFooterSpacer: {
          display: 'none',
        },
        buttonNext: {
          fontWeight: 700,
        },
      }}
      options={{ zIndex: 1015 }}
    />
  )
}

export default connect(
  (state) => ({
    visit: getProfileReportsJoyrideVisit(state),
    userId: getUserId(state),
    viewedTutorials: viewedTutorialsSelector(state),
  }),
  {
    setViewedTutorials: setViewedTutorialsAction,
  }
)(ProfileReportsJoyride)
